<script setup lang="ts">
import { computed, onMounted, ref } from "vue";

import actions from "~/actions";
import { ChevronDownIcon, ChevronUpIcon } from "~/icons";
import { EditorMode, NumberFormat } from "~/shared/enums";
import type { PropertyNumber } from "~/shared/types";
import { usePageStore } from "~/stores";
import { makeUuid, sanitizeIntegerInput } from "~/utils/common";
import { when } from "~/utils/wait";

const props = defineProps<{
  property: PropertyNumber;
  editorMode: EditorMode;
  value?: number | null;
}>();

const emit = defineEmits<{
  update: [value: number | null];
}>();

const pageStore = usePageStore();

const numberInput = ref<HTMLSpanElement | null>(null);
const isDollars = computed(() => props.property.adtl.format === NumberFormat.DOLLARS);
const isPercentage = computed(() => props.property.adtl.format === NumberFormat.PERCENTAGE);

const isFormMode = computed(() => props.editorMode === EditorMode.FORM);
const isAutomationMode = computed(() => props.editorMode === EditorMode.AUTOMATION);
const placeholder = computed(() => (isAutomationMode.value ? "(Unset)" : "Add number"));

const updateText = (e: Event) => {
  const value = sanitizeIntegerInput(e);
  emit("update", value === "" || value === "-" ? null : parseInt(value, 10));
};

const increment = () => {
  emit("update", (props.value ?? 0) + 1);
};

const decrement = () => {
  emit("update", (props.value ?? 0) - 1);
};

const onEnterPress = () => numberInput.value?.blur();

const id = ref(`number-editor-input-${makeUuid()}`);

const onClick = async () => {
  if (!isAutomationMode.value || !pageStore.isMobile) {
    return;
  }
  actions.visualization.openKeyboardIfIos();
  await when(numberInput);
  numberInput.value?.focus();
};

onMounted(async () => {
  if (isAutomationMode.value) {
    actions.visualization.openKeyboardIfIos();
    await when(numberInput);
    numberInput.value?.focus();
  }
});
</script>

<template>
  <div class="group/number-input relative w-full">
    <span v-if="isDollars && value !== null" class="pointer-events-none absolute top-1/2 left-2 -translate-y-1/2">
      $
    </span>
    <label :for="id" class="sr-only">Value</label>
    <input
      :id="id"
      ref="numberInput"
      type="text"
      inputmode="numeric"
      :value="value"
      :placeholder="placeholder"
      class="w-full break-words cursor-text rounded-sm border bg-std py-[5px] pr-2 text-sm hyphens-auto text-md caret-md focus-ring-none placeholder:text-vlt focus:border-primary-base dark:focus-ring-none dark:focus:border-primary-base"
      :class="{
        'border-0': isAutomationMode,
        'border-md hover:border-hvy': isFormMode,
        'pl-6': isDollars && value !== null,
        'pl-2': !isDollars || (isDollars && value === null),
      }"
      @click.stop="onClick"
      @input="updateText"
      @blur="updateText"
      @keydown.esc.stop.prevent="numberInput?.blur()"
      @keydown.enter.stop.prevent="onEnterPress"
      @keydown.stop />
    <div
      class="absolute inset-y-0 right-1.5 flex items-center gap-1.5 opacity-0 select-none group-focus-within/number-input:opacity-100 group-hover/number-input:opacity-100">
      <span v-if="isPercentage && value !== null" class="text-xs text-vlt">%</span>
      <div class="flex flex-col justify-center">
        <div
          class="cursor-pointer rounded-sm"
          :class="{
            'hover:bg-md': isFormMode,
            'text-vlt hover:bg-lt': isAutomationMode,
          }"
          @click="increment"
          @keydown.enter="increment">
          <ChevronUpIcon class="size-3" />
        </div>
        <div
          class="cursor-pointer rounded-sm"
          :class="{
            'hover:bg-md': isFormMode,
            'text-vlt hover:bg-lt': isAutomationMode,
          }"
          @click="decrement"
          @keydown.enter="decrement">
          <ChevronDownIcon class="size-3" />
        </div>
      </div>
    </div>
  </div>
</template>
